import { CheckCircle2, Circle, Quote, ArrowRight } from "lucide-react";
import { ScoreRing, CompetencyBar, TrustBadge } from "@/components/shared";
import { Note } from "./parts";

function timeline(a) {
  const st = a.assessment?.status;
  const beyond = ["screening", "interview", "offer", "hired", "rejected"].includes(a.stage);
  const ev = [
    { label: "Applied", at: a.applied_at, done: true },
    { label: "Profile confirmed", done: !!a.parsed_profile?.cv_confirmed || beyond },
    { label: "Consent recorded", done: !!a.consent?.data_processing },
    { label: "Assessment submitted", done: st === "scoring" || st === "completed" },
    { label: "Assessment scored", done: a.overall_score != null },
  ];
  if (["interview", "offer", "hired"].includes(a.stage)) ev.push({ label: "Interview stage", done: true });
  if (["offer", "hired"].includes(a.stage)) ev.push({ label: "Offer extended", done: true });
  if (a.stage === "hired") ev.push({ label: "Hired", done: true });
  if (a.stage === "rejected") ev.push({ label: "Application closed", done: true });
  const next = st === "not_started" || st === "in_progress" ? "Finish your assessment"
    : st === "scoring" ? "Awaiting your score — a person reviews every result"
      : a.stage === "interview" ? "Prepare for your interview"
        : a.stage === "offer" ? "Respond to your offer"
          : a.stage === "hired" ? "You were hired — congratulations!"
            : a.stage === "rejected" ? "This application is closed" : "Recruiter review in progress";
  return { ev, next };
}

function Timeline({ a }) {
  const { ev, next } = timeline(a);
  return (
    <div className="space-y-2" data-testid={`timeline-${a.id}`}>
      <h3 className="text-sm font-semibold mb-1">Activity</h3>
      {ev.map((e) => (
        <div key={e.label} className="flex items-center gap-2.5 text-sm">
          {e.done ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
          <span className={e.done ? "" : "text-muted-foreground"}>{e.label}</span>
          {e.at && <span className="ml-auto text-[11px] hs-mono text-muted-foreground">{new Date(e.at).toLocaleDateString()}</span>}
        </div>
      ))}
      <div className="mt-2 rounded-lg bg-primary/5 border border-primary/20 p-2.5 text-xs text-primary flex items-center gap-1.5" data-testid={`timeline-next-${a.id}`}>
        <ArrowRight className="h-3.5 w-3.5 shrink-0" /> Next: {next}
      </div>
    </div>
  );
}

function ScoredResult({ a }) {
  return (
    <div className="space-y-4" data-testid={`result-${a.id}`}>
      <div className="flex items-center gap-3">
        <ScoreRing score={Math.round(a.overall_score)} size={52} label="score" />
        <div>
          <div className="text-sm font-semibold">Assessment complete</div>
          {a.confidence && <TrustBadge type={a.confidence === "high" ? "high" : "medium"} className="mt-1">{a.confidence} confidence</TrustBadge>}
        </div>
      </div>
      {a.competencies?.length > 0 && <div className="space-y-3">{a.competencies.map((c, i) => <CompetencyBar key={i} {...c} />)}</div>}
      {a.evidence?.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Evidence from your answers</div>
          {a.evidence.slice(0, 3).map((e, i) => (
            <div key={i} className="p-2.5 rounded-lg bg-amber-500/10 border-l-4 border-amber-500 flex items-start gap-2">
              <Quote className="h-3 w-3 text-amber-500 mt-1 shrink-0" />
              <p className="text-xs text-amber-900 dark:text-amber-100 hs-mono leading-relaxed">"{e.quote}"</p>
            </div>
          ))}
        </div>
      )}
      {a.rationale && <p className="text-sm text-muted-foreground leading-relaxed"><span className="text-foreground font-medium">AI rationale · human reviewed: </span>{a.rationale}</p>}
    </div>
  );
}

export function ResultDetail({ a }) {
  const st = a.assessment?.status;
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
      <div className="space-y-4 min-w-0">
        {st === "scoring" && <Note>Your assessment is being scored by AI, with a person reviewing every result. Check back shortly.</Note>}
        {st === "failed" && <Note tone="rose">Scoring didn't complete. This won't affect your application — the team will re-run it.</Note>}
        {a.overall_score != null ? <ScoredResult a={a} /> : !["scoring", "failed"].includes(st) && <p className="text-sm text-muted-foreground">No assessment result yet.</p>}
      </div>
      <Timeline a={a} />
    </div>
  );
}
